import { Dispatch } from "redux";
import type { RootState } from "app/startup/store";
import { getCentreList } from "./selectors";
import { getLocalApi } from "app/shared/api/localApi";
import apiConfig from "app/api/apiConfig.json";
import { ApiErrors } from "app/shared/api/ApiErrors";
import type { DivisionReportTextDto } from "app/shared/api/getDivision";
import { normalizeDivisionReportTextDto } from "app/shared/api/getDivision";

// ---- Types ----------------------------------------------------
export type Id = number | string;

export type EmailString = string;
export type PhoneString = string;

export interface Address {
  id?: number | string;
  building?: string;
  street?: string;
  postalCode?: string;
  city?: string;
  country?: string;
  countryISOCode?: string;
}

export interface PersonWithSignature {
  id?: number | string;
  name?: string;
  email?: EmailString;
  signatureCode?: string | null;
  signatureImage?: string | null;
  signatureContentType?: string | null;
  signatureFileName?: string | null;
}

export interface BankAddress extends Address {}

export interface BankDetails {
  id?: number | string;
  beneficiaryName?: string;
  accountNo?: string;
  bankName?: string;
  iban?: string;
  swiftCode?: string;
  branchCode?: string;
  abaRoutingNo?: string;
  achAba?: string;
}

export interface IntermediaryBankDetails {
  bankName?: string;
  swiftCode?: string;
  address?: BankAddress;
}

export interface Centre {
  id?: Id;
  active: boolean;
  hasActiveSchools: boolean;
  entityUpdate: boolean;

  // Header
  centreName: string;
  code: string;
  country?: string;
  countryISOCode?: string;
  currency?: string;
  centreCode?: string;
  generalEmail?: EmailString;
  telephoneNo?: PhoneString;
  transferEmergencyNo?: PhoneString;
  emergencyNo?: PhoneString;
  emergencyTelephone?: PhoneString;
  accommodationEmail?: EmailString;
  schoolSponsorshipNumber?: string;
  physicalCentre?: boolean;
  printAs?: "A4" | "Letter";
  brandColorHex?: string;

  //BI 
  guarantees?: number;
  individualsRatio?: number;
  staffingRatio?: number;
  emptyBeds?: number;

  // Images
  logoCode?: string | null;
  logo?: {
    image?: string;
    contentType?: string;
    fileName?: string;
  } | null;

  // People
  centreDirector?: PersonWithSignature;
  directorOfStudies?: PersonWithSignature;

  // Addresses
  centreAddress?: Address;

  // Tax / Registration
  vatNumber?: string;
  registrationNumber?: string;
  vatExemptionNumber?: string;
  chequePayableTo?: string;

  // Banking
  bankDetails?: BankDetails;
  bankAddress?: BankAddress;
  beneficiaryBankAddress?: BankAddress;
  intermediaryBankDetails?: IntermediaryBankDetails;

  reportTexts?: DivisionReportTextDto[];
}

// ---- Action Types ---------------------------------------------
export const FETCH_CENTRES = "FETCH_CENTRES";
export const SET_CENTRES = "SET_CENTRES";

export const SAVE_CENTRE_REQUEST = "SAVE_CENTRE_REQUEST";
export const SAVE_CENTRE_SUCCESS = "SAVE_CENTRE_SUCCESS";
export const SAVE_CENTRE_FAILURE = "SAVE_CENTRE_FAILURE";

export const CENTRE_SET_FILTER = "CENTRE_SET_FILTER";
export const CENTRE_SET_SHOW_ALL = "CENTRE_SET_SHOW_ALL";

export const CENTRE_MODAL_OPEN = "CENTRE_MODAL_OPEN";
export const CENTRE_MODAL_CLOSE = "CENTRE_MODAL_CLOSE";

// ---- Simple Action Creators -----------------------------------
export const fetchCentresAction = () => ({ type: FETCH_CENTRES } as const);

export const setCentres = (payload: Centre[]) =>
  ({ type: SET_CENTRES, payload } as const);

export const saveCentreRequest = () =>
  ({ type: SAVE_CENTRE_REQUEST } as const);
export const saveCentreSuccess = () =>
  ({ type: SAVE_CENTRE_SUCCESS } as const);
export const saveCentreFailure = (error: string) =>
  ({ type: SAVE_CENTRE_FAILURE, error } as const);

export const setCentreFilter = (name: string) =>
  ({ type: CENTRE_SET_FILTER, payload: name } as const);

export const setCentreShowAll = (show: boolean) =>
  ({ type: CENTRE_SET_SHOW_ALL, payload: show } as const);

export const openCentreModal = (editing?: Centre | null) =>
  ({ type: CENTRE_MODAL_OPEN, payload: editing ?? null } as const);

export const closeCentreModal = () =>
  ({ type: CENTRE_MODAL_CLOSE } as const);

// ---- Helpers / Normalizers ------------------------------------
const toBool = (value: any, defaultValue = false): boolean => {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    const trimmed = value.trim().toLowerCase();
    if (!trimmed) return defaultValue;
    if (["true", "1", "yes", "y"].includes(trimmed)) return true;
    if (["false", "0", "no", "n"].includes(trimmed)) return false;
  }
  if (typeof value === "number") return value !== 0;
  return defaultValue;
};

const normalizePrintAs = (value: any): "A4" | "Letter" => {
  if (value === 2 || `${value}`.toLowerCase() === "letter") return "Letter";
  return "A4";
};

const normalizeHexColor = (value: any): string | undefined => {
  if (value === null || value === undefined) return undefined;
  const raw = String(value).trim();
  if (!raw) return undefined;
  const withoutHash = raw.startsWith("#") ? raw.slice(1) : raw;
  if (!withoutHash) return undefined;
  if (![3, 6].includes(withoutHash.length)) return undefined;
  if (!/^[0-9a-fA-F]+$/.test(withoutHash)) return undefined;
  return `#${withoutHash.toUpperCase()}`;
};

const pickFirst = <T>(...candidates: T[]): T | undefined => {
  return candidates.find((candidate) => {
    if (candidate === null || candidate === undefined) return false;
    if (typeof candidate === "string" && candidate.trim() === "") return false;
    return true;
  });
};

const normalizeIso2 = (value: any): string | undefined => {
  if (value === null || value === undefined) return undefined;
  const str = String(value).trim().toUpperCase();
  if (!str) return undefined;
  return /^[A-Z]{2}$/.test(str) ? str : undefined;
};

const toAddress = (input: any | undefined): Address | undefined => {
  if (!input || typeof input !== "object") return undefined;
  const isoCandidate = pickFirst(
    input.countryISOCode,
    input.countryCode,
    input.isoCode,
    input.iso2,
    input.alpha2
  );
  const normalizedIso = normalizeIso2(isoCandidate);
  return {
    id: pickFirst(input.id, input.addressId, input.addressID),
    building: pickFirst(input.building, input.buildingOrApartment, input.line1, input.addressLine1),
    street: pickFirst(input.street, input.line2, input.addressLine2),
    postalCode: pickFirst(input.postalCode, input.line3, input.addressLine3, input.zipCode, input.postCode),
    city: pickFirst(input.city, input.line4, input.addressLine4, input.town),
    country: normalizedIso ?? pickFirst(input.country, input.countryName, input.countryCode),
    countryISOCode: normalizedIso,
  };
};

const getContactTypeNumber = (value: any): number | undefined => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = parseInt(value.trim(), 10);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
};

const normalizeContacts = (input: any): { director?: PersonWithSignature; dos?: PersonWithSignature } => {
  const contacts = Array.isArray(input) ? input : [];
  const normalizeSignature = (value: any): string | null => {
    if (typeof value === "string" && value.trim()) return value;
    return null;
  };

  const directorCandidate =
    contacts.find((c) => getContactTypeNumber(c?.contactType) === 1) ??
    contacts.find((c) => getContactTypeNumber(c?.contactType) === 0) ??
    contacts.find((c) => `${c?.type}`.toLowerCase() === "director");

  const dosCandidate =
    contacts.find((c) => getContactTypeNumber(c?.contactType) === 2) ??
    contacts.find(
      (c) => getContactTypeNumber(c?.contactType) === 1 && c !== directorCandidate
    ) ??
    contacts.find((c) => `${c?.type}`.toLowerCase() === "directorofstudies");

  const director: PersonWithSignature | undefined = directorCandidate
    ? {
        id: pickFirst(directorCandidate.id, directorCandidate.contactId, directorCandidate.contactID),
        name: pickFirst(directorCandidate.name, directorCandidate.fullName),
        email: pickFirst(directorCandidate.email, directorCandidate.mail),
        signatureCode: normalizeSignature(
          pickFirst(
            directorCandidate.signatureCode,
            directorCandidate.signatureImage,
            directorCandidate.signatureBase64
          )
        ),
        signatureImage: pickFirst(
          directorCandidate.signatureImage,
          directorCandidate.signatureCode,
          directorCandidate.signatureBase64
        ),
        signatureContentType: pickFirst(
          directorCandidate.signatureContentType,
          directorCandidate.signatureMimeType,
          directorCandidate.contentType
        ),
        signatureFileName: pickFirst(
          directorCandidate.signatureFileName,
          directorCandidate.signatureName,
          directorCandidate.fileName
        ),
      }
    : undefined;

  const dos: PersonWithSignature | undefined = dosCandidate
    ? {
        id: pickFirst(dosCandidate.id, dosCandidate.contactId, dosCandidate.contactID),
        name: pickFirst(dosCandidate.name, dosCandidate.fullName),
        email: pickFirst(dosCandidate.email, dosCandidate.mail),
        signatureCode: normalizeSignature(
          pickFirst(
            dosCandidate.signatureCode,
            dosCandidate.signatureImage,
            dosCandidate.signatureBase64
          )
        ),
        signatureImage: pickFirst(
          dosCandidate.signatureImage,
          dosCandidate.signatureCode,
          dosCandidate.signatureBase64
        ),
        signatureContentType: pickFirst(
          dosCandidate.signatureContentType,
          dosCandidate.signatureMimeType,
          dosCandidate.contentType
        ),
        signatureFileName: pickFirst(
          dosCandidate.signatureFileName,
          dosCandidate.signatureName,
          dosCandidate.fileName
        ),
      }
    : undefined;

  return { director, dos };
};

const normalizeBankDetails = (input: any | undefined): BankDetails | undefined => {
  if (!input || typeof input !== "object") return undefined;
  return {
    id: pickFirst(input.id, input.bankDetailsId, input.bankDetailId),
    beneficiaryName: pickFirst(input.beneficiaryName, input.beneficiary),
    accountNo: pickFirst(input.accountNo, input.accountNumber, input.account),
    bankName: pickFirst(input.bankName, input.name),
    iban: pickFirst(input.iban, input.ibanNumber),
    swiftCode: pickFirst(input.swiftCode, input.swift),
    branchCode: pickFirst(input.branchCode, input.sortCode),
    abaRoutingNo: pickFirst(input.abaRoutingNo, input.abaRoutingNumber),
    achAba: pickFirst(input.achAba, input.achNumber, input.achAbaNumber),
  };
};

const normalizeIntermediaryDetails = (input: any | undefined): IntermediaryBankDetails | undefined => {
  if (!input || typeof input !== "object") return undefined;
  return {
    bankName: pickFirst(input.bankName, input.name),
    swiftCode: pickFirst(input.swiftCode, input.swift),
    address: toAddress(input.address),
  };
};

const ensureCentreFields = (raw: any): Centre => {
  const centre: any = { ...raw };

  centre.id = pickFirst(raw.id, raw.centreId, raw.centreID, raw.identifier, raw.code);
  centre.centreName = String(pickFirst(raw.centreName, raw.name, raw.title, raw.centre)) || "";
  centre.centreCode = String(pickFirst(raw.centreCode, raw.code, raw.id, raw.identifier) ?? "").toUpperCase();

  const countryFallback = pickFirst(
    raw.country,
    raw.countryName,
    raw.countryLabel,
    raw.countryCode,
    raw.countryId,
    raw.countryISOCode
  );
  centre.country = typeof countryFallback === "string" ? countryFallback : "";
  const countryIso = normalizeIso2(
    pickFirst(
      raw.countryISOCode,
      raw.countryCode,
      raw.countryId,
      (centre.country ?? "").toString()
    )
  );
  if (countryIso) {
    centre.countryISOCode = countryIso;
  }

  centre.currency = pickFirst(raw.currency, raw.currencyCode);
  centre.generalEmail = pickFirst(raw.generalEmail, raw.email, raw.contactEmail);
  centre.accommodationEmail = pickFirst(raw.accommodationEmail, raw.accommodationMail);
  centre.telephoneNo = pickFirst(raw.telephoneNo, raw.telephone, raw.phone);
  centre.transferEmergencyNo = pickFirst(
    raw.transferEmergencyNo,
    raw.transferEmergencyNumber,
    raw.transferEmergencyTelephone
  );
  const emergencyContact = pickFirst(raw.emergencyNo, raw.emergencyTelephone, raw.emergencyPhone);
  centre.emergencyNo = emergencyContact;
  centre.emergencyTelephone = emergencyContact;
  centre.schoolSponsorshipNumber = pickFirst(raw.schoolSponsorshipNumber, raw.sponsorshipNumber);
  centre.physicalCentre = toBool(pickFirst(raw.physicalCentre, raw.isPhysicalCentre), false);
  centre.printAs = normalizePrintAs(pickFirst(raw.printAs, raw.printFormat));
  centre.active = toBool(pickFirst(raw.active, raw.isActive, raw.IsActive), true);
  centre.hasActiveSchools = raw.hasActiveSchools;
  const brandColorCandidate = pickFirst(
    raw.brandColorHex,
    raw.brandColourHex,
    raw.brandColour,
    raw.brandColor,
    raw.brandColourCode,
    raw.brandColorCode,
    centre.brandColorHex
  );
  const normalizedBrandColor = normalizeHexColor(brandColorCandidate);
  if (normalizedBrandColor) {
    centre.brandColorHex = normalizedBrandColor;
  } else {
    delete centre.brandColorHex;
  }

  const parseOptionalPct = (value: any): number | undefined => {
    if (value === null || value === undefined || value === "") return undefined;
    const n = typeof value === "number" ? value : Number(String(value).trim());
    return Number.isFinite(n) ? n : undefined;
  };
  const g = parseOptionalPct(raw.guarantees ?? raw.Guarantees);
  const ir = parseOptionalPct(raw.individualsRatio ?? raw.IndividualsRatio);
  const sr = parseOptionalPct(raw.staffingRatio ?? raw.StaffingRatio);
  const eb = parseOptionalPct(raw.emptyBeds ?? raw.EmptyBeds);
  delete centre.guarantees;
  delete centre.individualsRatio;
  delete centre.staffingRatio;
  delete centre.emptyBeds;
  if (g !== undefined) centre.guarantees = g;
  if (ir !== undefined) centre.individualsRatio = ir;
  if (sr !== undefined) centre.staffingRatio = sr;
  if (eb !== undefined) centre.emptyBeds = eb;

  centre.logoCode = pickFirst(raw.logoCode, raw.logo, raw.logoImage) ?? null;
  const logoCandidate = pickFirst(raw.logo, raw.logoImage, raw.logoFile, raw.logoDto, centre.logo);
  if (logoCandidate && typeof logoCandidate === "object") {
    const image = pickFirst(
      logoCandidate.image,
      logoCandidate.value,
      logoCandidate.logoImage,
      logoCandidate.base64
    );
    if (image) {
      centre.logo = {
        image,
        contentType: pickFirst(logoCandidate.contentType, logoCandidate.mimeType),
        fileName: pickFirst(logoCandidate.fileName, logoCandidate.name),
      };
    } else {
      centre.logo = centre.logo ?? null;
    }
  } else if (typeof logoCandidate === "string" && logoCandidate.trim()) {
    centre.logo = { image: logoCandidate.trim() };
  } else if (!centre.logo) {
    centre.logo = null;
  }

  const { director, dos } = normalizeContacts(raw.contacts);
  centre.centreDirector = centre.centreDirector ?? director ?? {};
  centre.directorOfStudies = centre.directorOfStudies ?? dos ?? {};

  if (!centre.centreAddress) {
    const centreAddressCandidate = Array.isArray(raw.addresses)
      ? raw.addresses.find((a: any) => `${a?.addressType}`.toLowerCase() === "centre") ?? raw.addresses[0]
      : raw.address ?? undefined;
    centre.centreAddress = toAddress(centreAddressCandidate);
  } else {
    centre.centreAddress = toAddress(centre.centreAddress);
  }

  const bankDetailsRaw = pickFirst(raw.bankDetails, raw.bankDetail, centre.bankDetails);
  centre.bankDetails = normalizeBankDetails(bankDetailsRaw);

  const bankAddressRaw = pickFirst(
    raw.bankAddress,
    raw.bankAddressDto,
    bankDetailsRaw?.bankAddress,
    bankDetailsRaw?.bankAddressDto,
    centre.bankAddress
  );
  centre.bankAddress = toAddress(bankAddressRaw);

  const beneficiaryBankAddressRaw = pickFirst(
    raw.beneficiaryBankAddress,
    raw.beneficiaryBankAddressDto,
    bankDetailsRaw?.beneficiaryBankAddress,
    bankDetailsRaw?.beneficiaryBankAddressDto,
    centre.beneficiaryBankAddress
  );
  centre.beneficiaryBankAddress = toAddress(beneficiaryBankAddressRaw);

  const intermediaryDetailsRaw = pickFirst(
    raw.intermediaryBankDetails,
    raw.intermediaryDetails,
    bankDetailsRaw?.intermediaryBankDetails,
    bankDetailsRaw?.intermediaryDetails,
    centre.intermediaryBankDetails
  );
  const normalizedIntermediary = normalizeIntermediaryDetails(intermediaryDetailsRaw);
  const intermediaryBankName = pickFirst(
    normalizedIntermediary?.bankName,
    bankDetailsRaw?.intermediaryBankName,
    bankDetailsRaw?.intermediaryName,
    raw.intermediaryBankName,
    centre.intermediaryBankDetails?.bankName
  );
  const intermediarySwiftCode = pickFirst(
    normalizedIntermediary?.swiftCode,
    bankDetailsRaw?.intermediarySwiftCode,
    bankDetailsRaw?.intermediarySwift,
    raw.intermediarySwiftCode,
    centre.intermediaryBankDetails?.swiftCode
  );

  const intermediaryAddressRaw = pickFirst(
    raw.intermediaryBankAddress,
    raw.intermediaryBankAddressDto,
    intermediaryDetailsRaw?.address,
    bankDetailsRaw?.intermediaryBankAddress,
    bankDetailsRaw?.intermediaryBankAddressDto,
    centre.intermediaryBankDetails?.address
  );
  const intermediaryAddress = toAddress(intermediaryAddressRaw);

  if (intermediaryBankName || intermediarySwiftCode || intermediaryAddress) {
    centre.intermediaryBankDetails = {
      bankName: intermediaryBankName,
      swiftCode: intermediarySwiftCode,
      address: intermediaryAddress,
    };
  } else {
    centre.intermediaryBankDetails = undefined;
  }

  const reportTextCandidates =
    (Array.isArray(raw?.reportTexts) ? raw.reportTexts : null) ??
    (Array.isArray(raw?.centreReportTexts) ? raw.centreReportTexts : null) ??
    (Array.isArray(raw?.divisionReportTexts) ? raw.divisionReportTexts : null) ??
    (Array.isArray(raw?.reportText) ? raw.reportText : null) ??
    [];

  if (Array.isArray(reportTextCandidates) && reportTextCandidates.length > 0) {
    const centreIdParsed =
      typeof centre?.id === "number"
        ? centre.id
        : typeof centre?.id === "string"
          ? parseInt(String(centre.id).replace(/\D+/g, ""), 10)
          : NaN;
    const normalizedReportTexts = reportTextCandidates
      .map((item: any) => {
        const normalized = normalizeDivisionReportTextDto(item);
        const isDivisionScoped =
          normalized.divisionId !== null && normalized.divisionId !== undefined;
        if (
          !isDivisionScoped &&
          (normalized.centreId === null || normalized.centreId === undefined) &&
          Number.isFinite(centreIdParsed) &&
          centreIdParsed > 0
        ) {
          return { ...normalized, centreId: centreIdParsed };
        }
        return normalized;
      })
      .filter((item: DivisionReportTextDto) => {
        if (item.isDeleted) return false;
        if (item.divisionId !== null && item.divisionId !== undefined) return false;
        if (item.centreId === null || item.centreId === undefined) return false;
        if (Number.isFinite(centreIdParsed) && centreIdParsed > 0) {
          return item.centreId === centreIdParsed;
        }
        return true;
      });
    centre.reportTexts = normalizedReportTexts;
  } else {
    centre.reportTexts = [];
  }

  return centre as Centre;
};

const normalizeCentreCodeKey = (c: any): string => {
  const code = String(
    pickFirst(
      c?.centreCode,
      c?.CentreCode,
    ) ?? ""
  )
    .trim()
    .toUpperCase();
  return code ? `code:${code}` : "";
};

const normalizeCentreIdKey = (c: any): string => {
  const idCandidate = pickFirst(c?.id, c?.centreId, c?.centreID, c?.CentreId, c?.identifier);
  const digits = String(idCandidate ?? "").replace(/\D+/g, "");
  return digits ? `id:${digits}` : "";
};

const normalizeCentreNameCountryKey = (c: any): string => {
  const name = String(pickFirst(c?.centreName, c?.name, c?.CentreName) ?? "")
    .trim()
    .toLowerCase();
  if (!name) return "";
  const iso = normalizeIso2(
    pickFirst(c?.countryISOCode, c?.countryCode, c?.CountryCode, c?.country, c?.Country, c?.centreAddress?.countryISOCode, c?.centreAddress?.country)
  );
  return `name:${name}|iso:${iso ?? ""}`;
};

const centresEquivalent = (a: any, b: any): boolean => {
  const aCode = normalizeCentreCodeKey(a);
  const bCode = normalizeCentreCodeKey(b);
  if (aCode && bCode && aCode === bCode) return true;

  const aId = normalizeCentreIdKey(a);
  const bId = normalizeCentreIdKey(b);
  if (aId && bId && aId === bId) return true;

  // Fall back to name+country match when code/id aren't reliable across endpoints.
  const aNameCountry = normalizeCentreNameCountryKey(a);
  const bNameCountry = normalizeCentreNameCountryKey(b);
  if (aNameCountry && bNameCountry && aNameCountry === bNameCountry) return true;

  return false;
};

const normalizeCentresResponse = (payload: any): Centre[] => {
  const candidates: any[] = [];

  if (Array.isArray(payload)) {
    candidates.push(payload);
  } else if (payload && typeof payload === "object") {
    const possibleArrays = [
      (payload as any).items,
      (payload as any).data,
      (payload as any).centres,
      (payload as any).value,
      (payload as any).results,
      (payload as any).records,
    ];
    possibleArrays.forEach((arr) => {
      if (Array.isArray(arr)) {
        candidates.push(arr);
      }
    });
  }

  const source = candidates.find((arr) => Array.isArray(arr)) ?? [];
  return (source as any[])
    .filter((item) => item && typeof item === "object")
    .map(ensureCentreFields);
};

// ---- Endpoint helper ------------------------------------------
const endpointPath = (
  key: string,
  fallback: string,
  params?: Record<string, string>
): string => {
  const tmpl = ((apiConfig as any)?.[key] as string) || fallback;
  if (!params) return tmpl;
  return Object.keys(params).reduce(
    (acc, k) => acc.replace(new RegExp(`{${k}}`, "g"), params[k]),
    tmpl
  );
};

// ---- Thunks ---------------------------------------------------
const slugifyCountryKey = (value: string): string =>
  value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "item";

export const fetchCentres = () => async (dispatch: Dispatch, getState: () => RootState) => {
  dispatch(fetchCentresAction());
  try {
    const api = await getLocalApi();
    const res = await api.call((apiConfig as any).centres_get_all ?? "Centres", "GET", null);
    if (!res?.ok) throw new Error("Failed to fetch centres");
    const raw = await res.json();
    const centres = normalizeCentresResponse(raw ?? []);

    // Keep any locally-added pending centres visible until backend publish them.
    const currentList = getCentreList(getState());

    const pending = (Array.isArray(currentList) ? currentList : [])
      .filter((c) => c && (c as any).entityUpdate === true)
      .filter((c) => {
        return !centres.some((x) => centresEquivalent(c, x));
      });

    dispatch(setCentres([...centres, ...pending]));
  } catch (e: any) {
    dispatch(setCentres([]));
    dispatch(saveCentreFailure(e?.message ?? "Failed to load centres"));
  }
};

const extractCentreIdForEndpoint = (value: any): string | undefined => {
  if (value === null || value === undefined) return undefined;
  const trimmed = String(value).trim();
  if (!trimmed) return undefined;
  const digitsOnly = trimmed.replace(/\D+/g, "");
  if (!digitsOnly) return undefined;
  const parsed = parseInt(digitsOnly, 10);
  if (!Number.isFinite(parsed) || parsed <= 0) return undefined;
  return String(parsed);
};

export const fetchCentreMerged =
  (centre: Centre) =>
  async (_dispatch: Dispatch): Promise<Centre> => {
    let merged: Centre = centre;
    const centreIdForEndpoint =
      extractCentreIdForEndpoint(centre?.id) ??
      extractCentreIdForEndpoint(centre?.centreCode);
    if (!centreIdForEndpoint) return merged;

    try {
      const api = await getLocalApi();
      const path = endpointPath(
        "get_centre",
        "Centres/{id}",
        { id: encodeURIComponent(centreIdForEndpoint) }
      );

      const res = await api.call(path, "GET", null);
      if (!res?.ok) return merged;

      const raw = await res.json();
      if (!raw || typeof raw !== "object") return merged;
      const normalized = ensureCentreFields(raw);
      merged = { ...centre, ...normalized };
    } catch {
    }
    return merged;
  };

export const saveCentre = (model: Centre) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    dispatch(saveCentreRequest());
    try {
      const api = await getLocalApi();
      const centreIdForEndpoint = extractCentreIdForEndpoint((model as any).centreId ?? model.id);
      const isUpdate = !!centreIdForEndpoint;

      const path = isUpdate
        ? endpointPath(
            "centre_update",
            "Centres/{id}",
            { id: encodeURIComponent(centreIdForEndpoint!) }
          )
        : endpointPath("centre_save", "Centres");

      const method = isUpdate ? "PUT" : "POST";
      const res = await api.call(path, method, JSON.stringify(model));

      if (!res) throw new Error("No response from API");
      if (!res.ok || (res.status !== 200 && res.status !== 201)) {
        const text = await res.text().catch(() => "");
        let parsed: any = null;
        if (text) {
          try {
            parsed = JSON.parse(text);
          } catch {
            parsed = text;
          }
        }
        const errors = ApiErrors.extractAllErrors(parsed, res, "Failed to save centre");
        const errorMessage = ApiErrors.formatErrors(errors);
        throw new Error(errorMessage);
      }

      // API returns the saved/created centre; optimistically add/update it in the list
      // and mark as "pending publish" so it stays locked until publishing completes.
      let createdRaw: any = null;
      try {
        createdRaw = await res.json();
      } catch {
        createdRaw = null;
      }
      const normalizedCreated = createdRaw && typeof createdRaw === "object"
        ? ensureCentreFields(createdRaw)
        : null;

      if (normalizedCreated) {
        const list = getCentreList(getState());

        const createdKey = normalizeCentreCodeKey(normalizedCreated) || normalizeCentreIdKey(normalizedCreated) || normalizeCentreNameCountryKey(normalizedCreated);
        const nextCentre: Centre = {
          ...normalizedCreated,
          entityUpdate: true,
        };

        const nextList = Array.isArray(list) ? list.slice() : [];
        const idx = createdKey ? nextList.findIndex((c) => centresEquivalent(c, normalizedCreated)) : -1;
        if (idx >= 0) nextList[idx] = { ...nextList[idx], ...nextCentre };
        else nextList.push(nextCentre);

        dispatch(setCentres(nextList));

        // Persist selection so CentreManager list keeps user on the relevant country after navigating back.
        try {
          const isoCandidate = pickFirst(
            (nextCentre as any)?.countryISOCode,
            (nextCentre as any)?.countryCode,
            (nextCentre as any)?.country,
            nextCentre?.centreAddress?.country
          );
          const iso = normalizeIso2(isoCandidate);
          const label =
            (iso ? iso : "") ||
            String(pickFirst((nextCentre as any)?.countryName, nextCentre.country, nextCentre.centreAddress?.country) ?? "");
          const key = iso ? iso : slugifyCountryKey(label || "Unknown Country");
          localStorage.setItem("centreManager.selectedCountryKey", key);
        } catch {
        }
      }

      dispatch(saveCentreSuccess());
      await (dispatch as any)(fetchCentres());
    } catch (e: any) {
      dispatch(saveCentreFailure(e?.message ?? "Failed to save centre"));
    }
  };

export type CentreActions =
  | ReturnType<typeof fetchCentresAction>
  | ReturnType<typeof setCentres>
  | ReturnType<typeof saveCentreRequest>
  | ReturnType<typeof saveCentreSuccess>
  | ReturnType<typeof saveCentreFailure>
  | ReturnType<typeof setCentreFilter>
  | ReturnType<typeof setCentreShowAll>
  | ReturnType<typeof openCentreModal>
  | ReturnType<typeof closeCentreModal>;